import { miniApp, openLink, openTelegramLink } from "@telegram-apps/sdk";
import { getPlatform, Platform } from "../platform/platform";

const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;

const normalizeUsername = (username?: string | null): string | undefined => {
    const u = username?.trim().replace(/^@/, "");
    return u && u.length > 0 ? u : undefined;
};

const pickByPlatform = (opts: { telegram?: string; max?: string }): string | undefined => {
    const platform = getPlatform();
    if (platform === Platform.Max) {
        if (nonEmpty(opts.max)) return opts.max;
        if (nonEmpty(opts.telegram)) return opts.telegram; // fallback for incomplete envs
        return undefined;
    }
    if (nonEmpty(opts.telegram)) return opts.telegram;
    if (nonEmpty(opts.max)) return opts.max; // fallback
    return undefined;
};

export const getBotName = (): string | undefined =>
    pickByPlatform({
        telegram: process.env.REACT_APP_TELEGRAM_BOT_NAME,
        max: process.env.REACT_APP_MAX_BOT_NAME,
    });

export const getBotLink = (): string | undefined => {
    const link = pickByPlatform({
        telegram: process.env.REACT_APP_TELEGRAM_BOT_LINK,
        max: process.env.REACT_APP_MAX_BOT_LINK,
    });
    if (nonEmpty(link)) return link;

    const name = getBotName();
    if (!name) return undefined;

    return getPlatform() === Platform.Max
        ? `https://max.ru/${name}`
        : `https://t.me/${name.replace(/^@/, "")}`;
};

export const getChannelLink = (): string | undefined =>
    pickByPlatform({
        telegram: process.env.REACT_APP_TELEGRAM_CHANNEL,
        max: process.env.REACT_APP_MAX_CHANNEL,
    });

export const getRunAppLink = (userId?: number): string | undefined => {
    const platform = getPlatform();

    if (platform === Platform.Max) {
        const botLink = getBotLink();
        if (!botLink) {
            const base = process.env.REACT_APP_MAX_RUN_APP_LINK;
            if (!base) return undefined;
            if (!userId) return base.replace(/\?startapp(?:=.*)?$/, "");
            const urlWithoutQuery = base.split("?")[0];
            return `${urlWithoutQuery}?start=ref${userId}`;
        }
        if (!userId) return botLink;
        return `${botLink}?start=ref${userId}`;
    }

    const base = pickByPlatform({
        telegram: process.env.REACT_APP_TELEGRAM_RUN_APP_LINK,
        max: process.env.REACT_APP_MAX_RUN_APP_LINK,
    });
    if (!base) return undefined;
    if (!userId) return base;
    // Telegram: "...?startapp=ref{userId}"
    return `${base}=ref${userId}`;
};

/** Профиль: Telegram/Desktop — `https://t.me/{username}`. (В Max профиль по ссылке отключен.) */
export const getUserProfileHref = (_userId: number, username?: string | null): string | undefined => {
    if (getPlatform() === Platform.Max) return undefined;
    const u = normalizeUsername(username);
    return u ? `https://t.me/${u}` : undefined;
};

/** Текст ссылки на профиль: `@username` при валидном username, иначе `name` (например Max без username). */
export const getUserProfileLinkLabel = (name: string, username?: string | null): string => {
    const u = normalizeUsername(username);
    return u ? `@${u}` : name;
};

const extractMaxSlug = (url: string): string | undefined => {
    try {
        const u = new URL(url);
        if (!/(\.|^)max\.ru$/i.test(u.hostname)) return undefined;
        const slug = u.pathname.replace(/^\/+/, "");
        return slug.length > 0 ? slug : undefined;
    } catch {
        const slug = String(url).replace(/^https?:\/\/(web\.)?max\.ru\//, "").replace(/^\/+/, "");
        return slug.length > 0 ? slug : undefined;
    }
};

const isTelegramHost = (hostname: string) => /(^|\.)t\.me$/i.test(hostname);

type MaxWebAppBridge = {
    openMaxLink?: (url: string) => void;
    openLink?: (url: string) => void;
    close?: () => void;
};

type TelegramWebAppBridge = {
    close?: () => void;
};

const getMaxWebApp = (): MaxWebAppBridge | undefined =>
    (window as Window & { WebApp?: MaxWebAppBridge }).WebApp;

const getTelegramWebApp = (): TelegramWebAppBridge | undefined =>
    (window as Window & { Telegram?: { WebApp?: TelegramWebAppBridge } }).Telegram?.WebApp;

const closeMiniApp = (): void => {
    const platform = getPlatform();

    if (platform === Platform.Telegram) {
        try {
            if (miniApp.close.isAvailable()) {
                miniApp.close();
                return;
            }
        } catch {
            // fall through
        }

        const tg = getTelegramWebApp();
        if (typeof tg?.close === "function") {
            tg.close();
        }
        return;
    }

    if (platform === Platform.Max) {
        const wa = getMaxWebApp();
        if (typeof wa?.close === "function") {
            wa.close();
        }
    }
};

const scheduleMaxClose = (): void => {
    // close() сразу после openMaxLink() отменяет переход в бота
    window.setTimeout(() => closeMiniApp(), 300);
};

const openMaxUrl = (url: string, closeApp = false): void => {
    const wa = getMaxWebApp();
    if (typeof wa?.openMaxLink === "function") {
        wa.openMaxLink(url);
        if (closeApp) scheduleMaxClose();
        return;
    }

    if (typeof wa?.openLink === "function") {
        wa.openLink(url);
        if (closeApp) scheduleMaxClose();
        return;
    }

    const slug = extractMaxSlug(url);
    window.location.href = slug ? `max://max.ru/${slug}` : url;
    if (closeApp) scheduleMaxClose();
};

/** Как в JoinChannelItem / JoinChannelPopup — через SDK Telegram. */
const openExternalLink = (url: string): void => {
    try {
        const { hostname } = new URL(url);

        if (getPlatform() === Platform.Telegram && isTelegramHost(hostname) && openTelegramLink.isAvailable()) {
            openTelegramLink(url);
            return;
        }

        if (openLink.isAvailable()) {
            openLink(url);
            return;
        }
    } catch {
        // fall through
    }

    window.location.href = url;
};

/**
 * Открывает ссылку с учетом платформы:
 * - Max: `WebApp.openMaxLink`, затем `openLink`, затем `max://`
 * - Telegram/Desktop: `openTelegramLink` / `openLink` (как в заданиях на подписку)
 */
export const openPlatformLink = (url: string): void => {
    const platform = getPlatform();
    if (platform === Platform.Max) {
        openMaxUrl(url);
        return;
    }
    openExternalLink(url);
};

const withTelegramStartParam = (url: string): string => {
    try {
        const parsed = new URL(url);
        if (!parsed.searchParams.has("start")) {
            parsed.searchParams.set("start", "");
        }
        return parsed.toString();
    } catch {
        return url.includes("?") ? `${url}&start=` : `${url}?start=`;
    }
};

export const openBotLink = (): boolean => {
    const url = getBotLink();
    if (!url) return false;

    const platform = getPlatform();

    if (platform === Platform.Telegram) {
        const startUrl = withTelegramStartParam(url);
        try {
            const { hostname } = new URL(startUrl);
            if (isTelegramHost(hostname) && openTelegramLink.isAvailable()) {
                openTelegramLink(startUrl);
                closeMiniApp();
                return true;
            }
        } catch {
            // fall through
        }

        openExternalLink(startUrl);
        closeMiniApp();
        return true;
    }

    if (platform === Platform.Max) {
        openMaxUrl(url, true);
        return true;
    }

    openExternalLink(url);
    return true;
};
